/**
 * Analytics Dashboard Page
 *
 * Main orchestrator for the analytics tab.
 * 6 sections: Overview | Funnels | Content | Revenue | Platforms | SEO
 * With period selector and Framer Motion page transitions.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, RefreshCw, Download } from 'lucide-react';
import {
  OverviewSection,
  FunnelSection,
  ContentSection,
  RevenueSection,
  PlatformSection,
  SEOSection,
  RealtimeSection,
  PeriodSelector,
} from './analytics';
import dashboardApi from '../../services/dashboardApi';
import {
  useDashboard,
  useRealtimeDashboard,
  useRevenueDashboard,
  useContentPerformance,
  usePlatformBreakdown,
} from '../../hooks/useDashboard';
import { useAuth } from '../../contexts/AuthContext';
import type { AnalyticsPeriod, DashboardSection } from '../../types/analytics';

const SECTIONS: { id: DashboardSection; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'funnels', label: 'Funnels' },
  { id: 'content', label: 'Content' },
  { id: 'revenue', label: 'Revenue' },
  { id: 'platforms', label: 'Platforms' },
  { id: 'seo', label: 'SEO' },
];

interface AnalyticsDashboardPageProps {
  onBack?: () => void;
  getAuthHeaders?: () => Promise<Record<string, string>>;
}

export const AnalyticsDashboardPage: React.FC<AnalyticsDashboardPageProps> = ({
  onBack,
}) => {
  const { getAuthToken } = useAuth();
  const [period, setPeriod] = useState<AnalyticsPeriod>('7d');
  const [activeSection, setActiveSection] = useState<DashboardSection>('overview');

  // Set token getter for API service
  useEffect(() => {
    dashboardApi.setTokenGetter(getAuthToken);
  }, [getAuthToken]);

  // Hooks for each section
  const { overview, trends, funnel, loading: dashboardLoading, refresh: refreshDashboard } = useDashboard(period);
  const { realtime, streamEvents, loading: realtimeLoading } = useRealtimeDashboard();
  const { revenue, loading: revenueLoading } = useRevenueDashboard();
  const { content, loading: contentLoading } = useContentPerformance(period);
  const { platforms, loading: platformsLoading } = usePlatformBreakdown(period);

  const handleExport = async () => {
    try {
      const blob = await dashboardApi.exportData('csv', period);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${period}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Export endpoint not yet available (Phase 4)
    }
  };

  const pageVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <OverviewSection
            overview={overview}
            trends={trends?.data || null}
            loading={dashboardLoading}
          />
        );
      case 'funnels':
        return <FunnelSection funnel={funnel} loading={dashboardLoading} />;
      case 'content':
        return <ContentSection content={content} loading={contentLoading} />;
      case 'revenue':
        return <RevenueSection revenue={revenue} loading={revenueLoading} />;
      case 'platforms':
        return <PlatformSection platforms={platforms} loading={platformsLoading} />;
      case 'seo':
        return <SEOSection />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            {onBack && (
              <button onClick={onBack} className="p-1">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <h1 className="text-lg font-bold text-gray-900">Analytics</h1>

            {/* Live indicator */}
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-[10px] text-emerald-600 font-medium">
                {realtime?.active_users || 0} active
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Export"
            >
              <Download className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={refreshDashboard}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${dashboardLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Section Tabs */}
        <div className="flex gap-1 px-4 pb-2 overflow-x-auto no-scrollbar">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                activeSection === s.id
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Period Selector */}
        <div className="px-4 pb-3">
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4 pb-24">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            {renderSection()}
          </motion.div>
        </AnimatePresence>

        {/* Realtime Stream (always visible at bottom of overview) */}
        {activeSection === 'overview' && (
          <div className="mt-4">
            <RealtimeSection
              realtime={realtime}
              streamEvents={streamEvents}
              loading={realtimeLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboardPage;
